import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { register as registerApi, validateReferralCode } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { UserPlus, ArrowLeft, Gift, CheckCircle, Wallet } from "lucide-react";

const registerSchema = z.object({
  username: z.string().min(3, "Username must be at least 3 characters"),
  email: z.string().email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string(),
  solWalletAddress: z.string().min(32, "Enter a valid Solana wallet address").max(44, "Enter a valid Solana wallet address"),
  referralCode: z.string().optional(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [referralValid, setReferralValid] = useState(null);
  const [referralInfo, setReferralInfo] = useState(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      referralCode: searchParams.get("ref") || "",
    },
  });

  const referralCode = watch("referralCode");

  // Validate referral code when it changes
  useEffect(() => {
    const validateCode = async () => {
      if (referralCode && referralCode.length >= 6) {
        try {
          const response = await validateReferralCode(referralCode);
          if (response.data.valid) {
            setReferralValid(true);
            setReferralInfo(response.data);
          } else {
            setReferralValid(false);
            setReferralInfo(null);
          }
        } catch {
          setReferralValid(false);
          setReferralInfo(null);
        }
      } else {
        setReferralValid(null);
        setReferralInfo(null);
      }
    };

    const debounce = setTimeout(validateCode, 500);
    return () => clearTimeout(debounce);
  }, [referralCode]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const response = await registerApi({
        username: data.username,
        email: data.email,
        password: data.password,
        referral_code: data.referralCode || null,
        sol_wallet_address: data.solWalletAddress,
      });
      
      loginUser(response.data.token, response.data.user);
      
      if (response.data.referral_bonus_applied) {
        toast.success(`Account created! You got a $5 referral bonus! 🎉`);
      } else {
        toast.success("Account created! Welcome to Lucky 7!");
      }
      navigate("/game");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grid-pattern flex items-center justify-center p-4">
      {/* Background Effects */}
      <div className="fixed top-0 left-0 w-full h-full pointer-events-none">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-[128px]" />
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[128px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        {/* Card */}
        <div className="glass-card rounded-2xl p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_31e6f2da-e30e-4604-a2ae-ae4870f67bdf/artifacts/m4cp44gx_passivepay_new_logo.png" 
              alt="PassivePay" 
              className="w-16 h-16 rounded-full object-cover mx-auto mb-4"
            />
            <h1 className="text-2xl font-heading font-bold">Create Account</h1>
            <p className="text-muted-foreground mt-2">
              Free PAY tokens on signup
            </p>
          </div>

          {/* Referral Bonus Banner */}
          {referralValid && referralInfo && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 rounded-lg bg-primary/10 border border-primary/30"
            >
              <div className="flex items-center gap-3">
                <Gift className="w-5 h-5 text-primary" />
                <div>
                  <div className="font-medium text-primary">
                    ${referralInfo.signup_bonus} Bonus Applied!
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Referred by {referralInfo.referrer_username}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                placeholder="lucky_player"
                {...register("username")}
                className="bg-background/50 border-white/10 focus:border-primary h-12"
                data-testid="register-username"
              />
              {errors.username && (
                <p className="text-sm text-destructive">{errors.username.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                {...register("email")}
                className="bg-background/50 border-white/10 focus:border-primary h-12"
                data-testid="register-email"
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                {...register("password")}
                className="bg-background/50 border-white/10 focus:border-primary h-12"
                data-testid="register-password"
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                {...register("confirmPassword")}
                className="bg-background/50 border-white/10 focus:border-primary h-12"
                data-testid="register-confirm-password"
              />
              {errors.confirmPassword && (
                <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="solWalletAddress" className="flex items-center gap-2">
                <Wallet className="w-4 h-4 text-primary" />
                Solana Wallet Address
              </Label>
              <Input
                id="solWalletAddress"
                placeholder="Enter your SOL address to receive free PAY tokens"
                {...register("solWalletAddress")}
                className="bg-background/50 border-white/10 focus:border-primary h-12 font-mono text-sm"
                data-testid="register-sol-wallet"
              />
              {errors.solWalletAddress && (
                <p className="text-sm text-destructive">{errors.solWalletAddress.message}</p>
              )}
              <p className="text-xs text-muted-foreground">
                You'll receive free PAY tokens at this address after signup
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="referralCode" className="flex items-center gap-2">
                <Gift className="w-4 h-4 text-secondary" />
                Referral Code (Optional)
              </Label>
              <div className="relative">
                <Input
                  id="referralCode"
                  placeholder="Enter referral code"
                  {...register("referralCode")}
                  className={`bg-background/50 border-white/10 focus:border-primary h-12 uppercase ${
                    referralValid === true ? "border-primary/50" : ""
                  } ${referralValid === false ? "border-destructive/50" : ""}`}
                  data-testid="register-referral-code"
                />
                {referralValid === true && (
                  <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary" />
                )}
              </div>
              {referralValid === false && referralCode && (
                <p className="text-sm text-destructive">Invalid referral code</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full neon-glow btn-glow font-heading font-bold uppercase tracking-wider h-12"
              data-testid="register-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <UserPlus className="w-5 h-5 mr-2" /> Create Account
                </>
              )}
            </Button>
          </form>

          {/* Login Link */}
          <p className="text-center text-muted-foreground mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-primary hover:underline font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
